import { PublicAxiosInstance } from '@/api/axios'
import ConfirmModal from '@/components/modal/confirm-modal'
import {
  PolicyAcknowledgementContent,
  PolicyOwnerDetails,
  type IAcknowledgementPolicy,
} from '@/components/policy/policy-acknowledgement-content'
import { PolicyAiSummaryCard } from '@/components/policy/policy-ai-summary-card'
import { AIButton } from '@/components/ui/ai-button'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FullPageSkeleton } from '@/components/ui/skeleton'
import { PolicyGenerationMethod } from '@/enum/global.enum'
import { usePolicyAiSummary } from '@/hooks/use-policy-ai-summary'
import { policyStatusColor, policyStatusLabel } from '@/utils/policy-status-label.utils'
import type { AxiosError } from 'axios'
import { Home } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Navigate, useParams } from 'react-router'
import { toast } from 'sonner'

interface PolicyData {
  policy: IAcknowledgementPolicy
  accessInfo: {
    accessedAt: string
    expiresAt: string
    isAcknowledged: boolean
  }
}

const getAccessErrorDisplay = (statusCode: number | null, message: string | null) => {
  if (statusCode === 400)
    return {
      title: 'Policy Not Available',
      message:
        message === 'Policy is not approved'
          ? 'This policy has not been approved yet and cannot be viewed for acknowledgement. Please contact your organisation administrator.'
          : 'This policy cannot be accessed at this time.',
    }

  if (statusCode === 401)
    return {
      title: 'Invalid Link',
      message: 'This link is invalid or has expired. Please request a new link from your administrator.',
    }

  if (statusCode === 404)
    return {
      title: 'Not Found',
      message: 'The policy you are looking for could not be found.',
    }

  return {
    title: 'Access Error',
    message: 'Failed to load policy. Please try again later.',
  }
}

const hasWrittenContent = (policy?: IAcknowledgementPolicy) =>
  !!policy?.description?.trim() &&
  (policy.generationMethod === PolicyGenerationMethod.MANUAL || policy.generationMethod === PolicyGenerationMethod.AI)

const PolicyReviewPage = () => {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [openModal, setOpenModal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [acknowledged, setAcknowledged] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [policyData, setPolicyData] = useState<PolicyData | null>(null)
  const [errorStatusCode, setErrorStatusCode] = useState<number | null>(null)
  const [isSummaryOpen, setIsSummaryOpen] = useState(false)

  useEffect(() => {
    const fetchPolicyData = async () => {
      try {
        setLoading(true)

        if (token) {
          // Handle secure token access
          const response = await PublicAxiosInstance.get(`/policy/secure/${token}`)
          setPolicyData(response.data.data)
        } else {
          setError('No policy identifier provided')
        }
      } catch (err) {
        const axiosError = err as AxiosError<{ message: string }>
        setErrorStatusCode(axiosError?.response?.status ?? null)
        setError(axiosError?.response?.data?.message ?? (err instanceof Error ? err.message : 'Failed to load policy'))
      } finally {
        setLoading(false)
      }
    }

    fetchPolicyData()
  }, [token])

  useEffect(() => {
    setAcknowledged(policyData?.accessInfo?.isAcknowledged ?? false)
  }, [policyData])

  const policyHistoryId = policyData?.policy?.policyHistoryId ?? ''
  const aiSummary = usePolicyAiSummary({ token: token ?? '', policyHistoryId })

  const handleSummarise = () => {
    setIsSummaryOpen(true)
    void aiSummary.summarise()
  }

  if (!token) return <Navigate to={'/'} />

  if (loading) return <FullPageSkeleton />

  if (error) {
    const { title, message } = getAccessErrorDisplay(errorStatusCode, error)

    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='text-red-600 text-6xl mb-4'>⚠️</div>
          <h2 className='text-2xl font-bold mb-2'>{title}</h2>
          <p className='text-gray-600 mb-4 max-w-md mx-auto'>{message}</p>
          <Button onClick={() => (window.location.href = '/')}>
            <Home className='h-4 w-4' />
            Go Home
          </Button>
        </div>
      </div>
    )
  }

  if (!policyData) return <Navigate to={'/'} />

  const { policy, accessInfo } = policyData
  const expiresAt = new Date(accessInfo.expiresAt)
  const isExpired = new Date() > expiresAt

  if (isExpired)
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='text-red-600 text-6xl mb-4'>⏰</div>
          <h2 className='text-2xl font-bold   mb-2'>Link Expired</h2>
          <p className='text-gray-600 mb-4'>This policy link has expired on {expiresAt.toLocaleDateString()}</p>
          <Button onClick={() => (window.location.href = '/')}>
            <Home className='h-4 w-4' />
            Go Home
          </Button>
        </div>
      </div>
    )

  const handleAcknowledgement = async () => {
    try {
      if (acknowledged) return
      await PublicAxiosInstance.post(`/policy/secure/${token}/acknowledgement`)
      setAcknowledged(true)
      toast.success('Policy acknowledged successfully')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge policy')
    }
  }

  const canSummarise = !!policyHistoryId && hasWrittenContent(policy)

  return (
    <div>
      <div className='min-h-screen flex flex-col'>
        {/* Fixed Header */}
        <div className='flex-shrink-0 p-4 border-b'>
          <div className='flex flex-wrap gap-2 items-center justify-between'>
            <div className='w-[100%]'>
              <div>
                <h1 className='text-xl font-bold '>{policy.name}</h1>
                <div className='flex items-center gap-2 mt-1'>
                  <Badge className={policyStatusColor(policy.status)}>{policyStatusLabel(policy.status)}</Badge>
                  <span className='text-sm text-gray-500'>{policy.policyCategory?.name}</span>
                </div>
              </div>
              <div className='flex flex-wrap items-center gap-2 justify-between'>
                <PolicyOwnerDetails owner={policy?.policyOwner} />
                {canSummarise && (
                  <AIButton
                    label='Summarise'
                    loadingLabel='Summarising...'
                    loading={aiSummary.isStreaming}
                    onClick={handleSummarise}
                    disabled={aiSummary.isStreaming}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className='flex-1 p-4 max-h-[calc(100vh-200px)] overflow-y-auto'>
          {canSummarise && isSummaryOpen && (
            <PolicyAiSummaryCard
              content={aiSummary.content}
              isStreaming={aiSummary.isStreaming}
              error={aiSummary.error}
              onClose={() => setIsSummaryOpen(false)}
            />
          )}
          <PolicyAcknowledgementContent policy={policy} />
        </div>

        {/* Fixed Checkbox at Bottom */}
        <div className='flex-shrink-0 p-4 border-t '>
          <div className='flex justify-center items-center'>
            <input
              id='acknowledge-checkbox'
              type='checkbox'
              checked={acknowledged}
              onChange={() => {
                if (acknowledged) return
                setOpenModal(true)
              }}
              className='h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary mr-3'
            />
            <label htmlFor='acknowledge-checkbox' className='text-sm  cursor-pointer'>
              I have read this policy and I acknowledge this policy
            </label>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={openModal}
        setOpen={setOpenModal}
        title='Confirm Policy Acknowledgement'
        description={`Confirm that you have read and understood the "${policy.name}".`}
        buttonLabel='Confirm Acknowledgement'
        loadingButtonLabel='Submitting acknowledgement...'
        isNested={true}
        hideToast
        onSubmit={async () => {
          setConfirmLoading(true)
          await handleAcknowledgement()
          setConfirmLoading(false)
        }}
        loading={confirmLoading}
      />
    </div>
  )
}

export default PolicyReviewPage
